import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, PLATFORM_ID, signal, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SearchItem } from '../../shared/models/search-item.model';
import { SearchIndexService } from '../../shared/services/search-index.service';
import { DeepNavigationService } from '../../shared/services/deep-navigation.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  protected searchTerm = '';
  protected debouncedSearchTerm = '';
  protected isSearchOpen = false;
  protected isSearchOverlayOpen = false;
  protected isScrolled = false;
  protected isHidden = false;
  protected isMobileMenuOpen = false;
  protected isCompanyExpanded = false;
  protected isInsightsExpanded = false;
  protected activeResultIndex = -1;
  protected showAnnouncement = signal(true);

  private lastScrollY = 0;
  private previouslyFocusedElement: HTMLElement | null = null;
  private readonly searchSubject = new Subject<string>();
  private resizeObserver: ResizeObserver | null = null;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const currentScrollY = window.scrollY;
    this.isScrolled = currentScrollY > 20;
    this.isHidden = currentScrollY > this.lastScrollY && currentScrollY > 140 && !this.isSearchOverlayOpen && !this.isMobileMenuOpen;
    this.lastScrollY = currentScrollY;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.isSearchOverlayOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSearchOverlay();
      } else if (event.key === 'Tab') {
        this.trapFocusInContainer(event, '.search-overlay');
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        const results = this.searchResults;
        if (results.length > 0) {
          this.activeResultIndex = (this.activeResultIndex + 1) % results.length;
          this.scrollToActiveResult();
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const results = this.searchResults;
        if (results.length > 0) {
          this.activeResultIndex = (this.activeResultIndex - 1 + results.length) % results.length;
          this.scrollToActiveResult();
        }
      } else if (event.key === 'Enter') {
        const results = this.searchResults;
        if (this.activeResultIndex >= 0 && this.activeResultIndex < results.length) {
          event.preventDefault();
          this.navigateFromSearch(results[this.activeResultIndex]);
        }
      }
    } else if (this.isMobileMenuOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMobileMenu();
      } else if (event.key === 'Tab') {
        this.trapFocusInContainer(event, '.mobile-menu-panel');
      }
    }
  }

  constructor(
    private readonly router: Router,
    private readonly searchIndexService: SearchIndexService,
    private readonly deepNavigationService: DeepNavigationService,
    private readonly elRef: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // When announcement visibility changes, update header height immediately
    effect(() => {
      this.showAnnouncement(); // track signal
      if (isPlatformBrowser(this.platformId)) {
        // Small delay to let DOM update before measuring
        setTimeout(() => this.updateHeaderHeight(), 10);
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      try {
        const dismissed = localStorage.getItem('articix_announcement_dismissed');
        if (dismissed === 'true') {
          this.showAnnouncement.set(false);
        }
      } catch {
        // Fallback for restricted storage environments
      }
    }

    this.searchSubject.pipe(
      debounceTime(150),
      distinctUntilChanged()
    ).subscribe(term => {
      this.debouncedSearchTerm = term;
      this.activeResultIndex = -1;
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeMobileMenu();
    });
  }

  protected dismissAnnouncement(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showAnnouncement.set(false);
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('articix_announcement_dismissed', 'true');
      } catch {
        // Fallback for restricted storage environments
      }
    }
  }

  protected onAnnouncementClick(event: MouseEvent, route: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([route]);
  }

  protected onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchTerm = target?.value ?? '';
    this.isSearchOpen = this.searchTerm.trim().length > 0;
    this.searchSubject.next(this.searchTerm);
  }

  protected get searchResults(): SearchItem[] {
    const term = this.debouncedSearchTerm.trim().toLowerCase();
    if (term.length < 2) {
      return [];
    }
    return this.searchIndexService.search(term);
  }

  protected navigateFromSearch(page: SearchItem | string): void {
    this.closeSearchOverlay();

    if (typeof page === 'string') {
      this.router.navigateByUrl(page);
      return;
    }

    const targetRoute = page.route;
    const sectionId = page.sectionId;

    const currentRoute = this.router.url.split('#')[0].split('?')[0];

    if (currentRoute === targetRoute) {
      if (sectionId) {
        this.deepNavigationService.scrollToAndHighlight(sectionId);
      }
    } else {
      const navigationExtras = sectionId ? { state: { scrollTo: sectionId } } : {};
      this.router.navigate([targetRoute], navigationExtras);
    }
  }

  protected openSearchOverlay(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    }
    this.isSearchOverlayOpen = true;
    this.searchTerm = '';
    this.debouncedSearchTerm = '';
    this.activeResultIndex = -1;
    this.searchIndexService.loadIndex().subscribe();
    this.lockScroll();

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const input = document.querySelector('.search-overlay-input') as HTMLInputElement;
        input?.focus();
      }, 200);
    }
  }

  protected closeSearchOverlay(): void {
    this.isSearchOverlayOpen = false;
    this.searchTerm = '';
    this.debouncedSearchTerm = '';
    this.activeResultIndex = -1;
    if (!this.isMobileMenuOpen) {
      this.unlockScroll();
    }
    if (isPlatformBrowser(this.platformId) && this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  protected isCompanySectionActive(): boolean {
    const url = this.router.url;
    return url.startsWith('/who-we-are') || url.startsWith('/investor-relations') || url.startsWith('/careers') || url.startsWith('/company/');
  }

  protected isInsightsSectionActive(): boolean {
    const url = this.router.url;
    return url.startsWith('/insights');
  }

  protected toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    if (this.isMobileMenuOpen) {
      if (isPlatformBrowser(this.platformId)) {
        this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
      }
      this.lockScroll();
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const firstLink = document.querySelector('.mobile-menu-panel a, .mobile-menu-panel button') as HTMLElement;
          firstLink?.focus();
        }, 200);
      }
    } else {
      this.closeMobileMenu();
    }
  }

  protected closeMobileMenu(): void {
    const wasOpen = this.isMobileMenuOpen;
    this.isMobileMenuOpen = false;
    this.isCompanyExpanded = false;
    this.isInsightsExpanded = false;

    if (!this.isSearchOverlayOpen) {
      this.unlockScroll();
    }

    if (wasOpen && isPlatformBrowser(this.platformId) && this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  protected toggleCompanySubmenu(): void {
    this.isCompanyExpanded = !this.isCompanyExpanded;
  }

  protected toggleInsightsSubmenu(): void {
    this.isInsightsExpanded = !this.isInsightsExpanded;
  }

  protected isHome(): boolean {
    return this.router.url === '/' || this.router.url.startsWith('/#') || this.router.url.startsWith('/home');
  }

  protected getTypeIcon(type: string): string {
    switch (type.toLowerCase()) {
      case 'service': return 'fa-gears';
      case 'insight': return 'fa-lightbulb';
      case 'career': return 'fa-briefcase';
      case 'contact': return 'fa-phone';
      case 'about': return 'fa-circle-info';
      default: return 'fa-file';
    }
  }

  private lockScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  private unlockScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  private trapFocusInContainer(event: KeyboardEvent, containerSelector: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const focusableElements = container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  private scrollToActiveResult(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => {
      const activeEl = document.querySelector('.search-result-card.keyboard-active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }
  protected navigateToAiAutomation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/ai-automation']);
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.updateHeaderHeight();
    const siteHeader = this.elRef.nativeElement.querySelector('.site-header') as HTMLElement | null;
    if (siteHeader && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
      this.resizeObserver.observe(siteHeader);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private updateHeaderHeight(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const siteHeader = this.elRef.nativeElement.querySelector('.site-header') as HTMLElement | null;
    const height = siteHeader?.getBoundingClientRect().height ?? 0;
    if (height > 0) {
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }

}
